import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import DataTable, { TableColumn } from 'react-data-table-component';
import { Button } from 'react-bootstrap';

import { CommissionShareOrder } from '@models/Commission';
import { OrderBadge } from '@components/Feature/Order';
import { tEnum } from '@components/i18n';
import { formatCurrency, formatPercentage } from '@utils/currency';

import { commissionSharesFetch, clearError } from './action';
import { selectCommissionShareList } from './selector';

const customStyles = {
  headCells: {
    style: {
      fontWeight: 'bold',
    },
  },
};

const CommissionList = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { commissionShares, loading, error, pagination } = useSelector(selectCommissionShareList);
  const perPage = pagination?.perPage ?? 20;
  const total = pagination?.total ?? 0;

  useEffect(() => {
    dispatch(commissionSharesFetch({ page: 1 }));
  }, [dispatch]);

  const handleRetry = () => {
    dispatch(clearError());
    dispatch(commissionSharesFetch({ page: 1 }));
  };

  const handlePageChange = (page: number) => {
    dispatch(commissionSharesFetch({ page }));
  };

  const handleRowsPerPageChange = (newPerPage: number, page: number) => {
    dispatch(commissionSharesFetch({ page, per_page: newPerPage }));
  };

  const columns: TableColumn<CommissionShareOrder>[] = useMemo(
    () => [
      {
        name: t('common:orderNumber'),
        selector: (row) => row.orderNumber,
        sortable: false,
      },
      {
        name: t('common:policyNumber'),
        selector: (row) => row.policyNumber || '-',
        sortable: false,
      },
      {
        name: t('common:insuranceType'),
        selector: (row) => tEnum('insuranceTypes', row.insuranceType),
        sortable: false,
      },
      {
        name: t('common:status'),
        cell: (row) => <OrderBadge status={row.status} />,
        sortable: false,
      },
      {
        name: t('common:totalPremium'),
        selector: (row) => formatCurrency(row.totalPremium),
        sortable: false,
        right: true,
      },
      {
        name: t('commissions.frontlineCommissionPercentage'),
        selector: (row) => formatPercentage(row.frontlineCommissionPercentage),
        sortable: false,
        right: true,
      },
      {
        name: t('commissions.commissionSharePercentage'),
        selector: (row) => formatPercentage(row.commissionSharePercentage),
        sortable: false,
        right: true,
      },
      {
        name: t('commissions.commissionAmount'),
        selector: (row) => formatCurrency(row.commissionAmount),
        sortable: false,
        right: true,
      },
    ],
    [t],
  );

  if (error) {
    return (
      <section>
        <h5 className="mb-4">{t('common:commissions')}</h5>
        <div className="alert alert-danger d-flex justify-content-between align-items-center">
          <span>{t('commissions.errorMessage')}</span>
          <Button variant="primary" size="sm" onClick={handleRetry}>
            {t('common:retry')}
          </Button>
        </div>
      </section>
    );
  }

  return (
    <section>
      <h5 className="mb-4">{t('common:commissions')}</h5>
      <DataTable
        columns={columns}
        data={commissionShares}
        customStyles={customStyles}
        fixedHeader
        pagination
        paginationServer
        progressPending={loading}
        paginationPerPage={perPage}
        paginationTotalRows={total}
        onChangePage={handlePageChange}
        onChangeRowsPerPage={handleRowsPerPageChange}
        noDataComponent={<div className="p-4">{t('common:noData')}</div>}
      />
    </section>
  );
};

export { CommissionList };
